import { createContext, useContext, useEffect, useMemo, useState } from "react";
import supabase from "../../supabaseClient/supabaseClient";
import { useAiAllowanceState } from "../../customhooks/useAiAllowanceState";
import { pickRegion } from "../../data/region";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [worksheetsCount, setWorksheetsCount] = useState(0);
  // Country and onboarding status from the profile; null until it loads.
  const [profile, setProfile] = useState(null);


  useEffect(() => {
    let active = true, observedAuthEvent = false;
    const fetchSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!active || observedAuthEvent) return;
      setIsAuthenticated(!!session?.access_token); // Check for access_token in session
      setUser(session?.user || null); // Store the user object or null if no session
    };
    
    fetchSession(); // Initial session check

    // Listen for auth state changes
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
        observedAuthEvent = true;
        if (!active) return;
        setIsAuthenticated(!!session?.access_token); // Update state based on auth changes
        setUser(session?.user || null);
    });

    // Cleanup listener on component unmount
    return () => {
      active = false;
      listener?.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const fetchWorksheetsCount = async () => {
      if (user) {
        const { data, error } = await supabase
          .from("profiles") // Adjust table name if different
          .select("credits, country, onboarding_completed")
          .eq("id", user.id) // Filter by the authenticated user
          .single(); // Fetch a single record
  
        if (error) {
          console.error("Error fetching worksheet count:", error);
        } else {
   
          setWorksheetsCount(data?.credits || 0);
          setProfile({
            country: data?.country || null,
            onboardingCompleted: Boolean(data?.onboarding_completed),
          });
        }
      }
    };
  
    fetchWorksheetsCount();
  }, [user]); // Fetch when user changes
  



  const aiAllowance = useAiAllowanceState(user?.id);

  const defaultRegion = useMemo(() => pickRegion(profile?.country), [profile?.country]);

  return (
    <AuthContext.Provider 
      value={{ 
        isAuthenticated,
        user,
        worksheetsCount,
        setWorksheetsCount,
        profile,
        defaultRegion,
        aiAllowance,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
